
// Vuex
import { createVuexHelpers } from 'vue2-helpers'

// Components
import IconSvg from '@/components/IconSvg.vue'
import Loader from '../components/Loaders/Loader.vue'
import QRCodeLoader from '../components/Loaders/QRCodeLoader.vue'
import TimeBadge from '@/components/TimeView/TimeBadge.vue'
import EventPeriod from '@/components/Ticketing/EventPeriod.vue'
import ColoredTextStatus from '@/components/Ticketing/ColoredTextStatus.vue'
import PrimaryButton from '@/components/Buttons/PrimaryButton.vue'
import EventDateIcon from '@/components/Ticketing/EventDateIcon.vue'

// API
import eventApi from '@/api/events'

// Icons
import VenueIcon from '@/assets/images/venue-icon.svg'
import CloseIcon from '@/assets/images/ic_cancel_grey.svg'

// Vue
import {
	computed,
	ComputedRef,
	defineComponent,
	onBeforeMount, onBeforeUnmount,
	onMounted,
	reactive,
	ref
} from 'vue'

// Router
import { useRouter, type Router, useRoute } from 'vue2-helpers/vue-router'
import { PurchasedOrder } from '@/types/apiTypes'
import {
	getFormattedDateTime,
	getFormattedTime,
	getValidationCode,
	getWSUrl
} from '@/utils/helpers'

// Constants
import {
	TIME_FORMATS,
	WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_CODE,
	WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_TEXT,
	WEB_SOCKET_STATE
} from '@/constants'

// Types
import {
	EventStatus,
	EXPIRED_STATUS,
	OrderStatus,
	PAYMENT_FAILED_CODES_TICKETING,
	TicketsStatus,
	TicketsUIStatus,
	TicketsUIStatusColor,
	ValidationMessageEnum,
	WebsocketEventTypes,
	WebsocketStringMessageEnum
} from '@/types/constantsTypes'
import { CatchError } from '@/types/commonTypes'
import { VenueInfoState } from '@/types/storeTypes'
import { AxiosError } from 'axios'

// Utils
import { getEventPeriod, getEventStartDateInfo } from '@/utils/dateUtils'
import { checkIOs, checkMacOS } from '@/utils/helpers'
import TicketingInfoModal from '@/components/modals/Ticketing/TicketingInfoModal.vue'

// Event Bus
import { EventBus } from '@/utils/eventBus'

export default defineComponent({
	name: 'Order',

	components: {
		TicketingInfoModal,
		EventDateIcon,
		PrimaryButton,
		ColoredTextStatus,
		TimeBadge,
		IconSvg,
		Loader,
		QRCodeLoader,
		EventPeriod
	},

	setup() {
		// Route
		const route = useRoute()
		const router: Router = useRouter()
		// WS
		let websocket = {} as WebSocket
		let isPingPongRunning = false

		// Vuex
		const { useGetters, useState } = createVuexHelpers()
		const {
			timeZoneName
		}: { timeZoneName: ComputedRef<string> } = useGetters('venue', [
			'timeZoneName'
		])
		const { venueInfo }: VenueInfoState = useState('venue', ['venueInfo'])
		const {
			isWebVersionAllowSmoothTransition
		}: {
			isWebVersionAllowSmoothTransition: ComputedRef<boolean>
		} = useGetters('venue', ['isWebVersionAllowSmoothTransition'])

		// Data
		const state = reactive({
			purchasedOrder: {} as PurchasedOrder,
			unavailablePopupData: {
				title: '',
				additionalText: ''
			}
		})

		// Refs
		const isLoading = ref(false)
		const pingTimeout = ref(0)
		const reconnectTimeout = ref(0)
		const paymentFailed = ref(false)

		// Computed
		const venueIcon = computed(() => VenueIcon)
		const closeIcon = computed(() => CloseIcon)
		const isOrderPending = computed(() => state.purchasedOrder.status === OrderStatus['PENDING'])
		const eventFullStartDate = computed(
			() =>
				`${state.purchasedOrder.event?.startDate || ''} ${state.purchasedOrder
					.event?.startTime || ''}`
		)
		const eventFullEndDate = computed(
			() =>
				`${state.purchasedOrder.event?.endDate || ''} ${state.purchasedOrder
					.event?.endTime || ''}`
		)
		const purchasedOrder = computed(() => ({
			...state.purchasedOrder,
			event: {
				...state.purchasedOrder.event,
				period: getEventPeriod({
					from: eventFullStartDate.value,
					to: eventFullEndDate.value
				}),
				eventStartDateInfo: getEventStartDateInfo(
					eventFullStartDate.value,
					state.purchasedOrder.event?.entryTime || ''
				),
				entryTime: getFormattedTime(
					state.purchasedOrder.event?.entryTime || '',
					null,
					TIME_FORMATS.AMPM
				)
			}
		}))

		const event = computed(() => purchasedOrder.value.event)
		const venue = computed(() => purchasedOrder.value.venue)
		const isEventPast = computed(() => event.value.status === EventStatus['PAST'])
		const fullName = computed(
			() =>
				`${purchasedOrder.value.firstName || ''} ${purchasedOrder.value
					.lastName || ''}`
		)
		const tickets = computed(() =>
			purchasedOrder.value?.tickets?.map(ticket => {
				const { actionTime } = ticket.onPaidDetails
				const isTicketPurchased = ticket.status === TicketsStatus['PURCHASED']
				const isTicketPending = ticket.status === TicketsStatus['PENDING_PAYMENT'] ||
					ticket.status === TicketsStatus['RESERVED']
				const isTicketIdVisible = !(isTicketPending ||
					ticket.status === TicketsStatus['CANCELED'] ||
					ticket.status === TicketsStatus['FAILED_PAY'])
				const linkToTabCreation = venueInfo.value.acceptForHere
					? `${venueInfo.value.onlineMenuUrl}/et/${ticket.id}`
					: ''
				const linkToTabTemplate = venueInfo.value.acceptForHere
					? `${venueInfo.value.onlineMenuUrl}/et-tab-template?customerNameToPrint=${fullName.value}&eventTicketId=${ticket.id}`
					: ''
				const isOrderButtonShown =
					ticket.status === TicketsStatus['REDEEMED'] && linkToTabCreation
				const statusName =
					(isTicketPurchased && isEventPast.value
						? EXPIRED_STATUS
						: TicketsUIStatus[ticket.status]) || ''

				// TODO: Fix TS error
				// eslint-disable-next-line
				// @ts-ignore
				const statusTheme =
					// eslint-disable-next-line
					// @ts-ignore
					(statusName && TicketsUIStatusColor[statusName]) || ''

				const paidTime = actionTime
					? getFormattedDateTime(
							actionTime,
							timeZoneName.value,
							'M/D/YYYY h:mm A'
					  )
					: ''
				return {
					...ticket,
					paidTime,
					statusName,
					statusTheme,
					isOrderButtonShown,
					linkToTabCreation,
					linkToTabTemplate,
					isTicketPending,
					isTicketIdVisible,
					purchased: isTicketPurchased,
				}
			}) || []
		)

		const isTicketsPlural = computed(() => tickets.value.length > 1)

		const addressLink = computed(() => {
			const { postalCode, region, city, address } = venue.value
			const addressString = `${address} ${city} ${region} ${postalCode}`
			// TODO: Replace checkIOs || checkMacOS with the ui/isMacOrIOS flag from the store
			// check if it exist before the component render
			const prefix = checkIOs() || checkMacOS()
				? 'https://maps.apple.com/?q='
				: 'https://maps.google.com/maps?q='
			return `${prefix}${addressString}`
		})

		// Hooks
		onBeforeMount(async (): Promise<void> => {
			// TODO: combine into 1 event with different params
			EventBus.$on('ticket-order-partial-payment', () => showPaymentFailedModal())
			EventBus.$on('ticket-order-paid-failed', () => showPaymentFailedModal())
			EventBus.$on('ticketing-service-error', () => showServiceErrorModal())
			EventBus.$on('update-purchase-order', () => getOrderTickets(true))
			await getOrderTickets()
		})

		onMounted(() => {
			if (isWebVersionAllowSmoothTransition.value) listenWebsockets()
		})

		onBeforeUnmount(() => {
			EventBus.$off('ticket-order-partial-payment')
			EventBus.$off('ticket-order-paid-failed')
			EventBus.$off('ticketing-service-error')
			EventBus.$off('update-purchase-order')
			reconnectTimeout.value && clearTimeout(reconnectTimeout.value)
			pingTimeout.value && clearTimeout(pingTimeout.value)
		})

		// Methods
		// TODO: Add websockets composable(4.0)
		const listenWebsockets = (isReconnect = false): void => {
			if (
				websocket &&
				(websocket.readyState === WEB_SOCKET_STATE.connecting ||
					websocket.readyState === WEB_SOCKET_STATE.open)
			) {
				websocket.close(
					WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_CODE,
					WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_TEXT
				)
			}

			const {
				params: { orderId }
			} = route
			const wsUri = `${getWSUrl()}/pubsub/eventPurchaseOrder/${orderId}`
			websocket = new WebSocket(wsUri)

			if (isReconnect) {
				getOrderTickets(true)
			}

			websocket.onclose = info => {
				isPingPongRunning = false

				if (
					info &&
					info.code === WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_CODE &&
					info.reason === WEB_SOCKET_CLOSE_PREVIOUS_CONNECTION_TEXT
				) {
					return
				}

				reconnectTimeout.value = setTimeout(() => {
					listenWebsockets(true)
				}, 3000)
			}

			websocket.onopen = () => {
				isPingPongRunning = true
				websocket.send(WebsocketStringMessageEnum.ping)
			}

			websocket.onmessage = e => {
				if (e.data === WebsocketStringMessageEnum.pong) {
					if (isPingPongRunning) {
						pingTimeout.value = setTimeout(() => {
							websocket.send(WebsocketStringMessageEnum.ping)
						}, 5000)
					}

					return
				}

				const data = JSON.parse(e.data)
				switch (WebsocketEventTypes[data.type]) {
					case 'EVENT_TICKET_UPDATED':
						getOrderTickets(true)
						break

					default:
							break
				}
			}
			websocket.onerror = () => {
				console.log('-------WEBSOCKET ERROR---')
			}
		}

		const getOrderTickets = async (withoutLoading = false): Promise<void> => {
			const {
				params: { subDomain, venueChainId, orderId }
			} = route

			if (!venueChainId || !subDomain || !orderId) return

			try {
				if (!withoutLoading) {
					isLoading.value = true
				}

				const res = await eventApi.getEventPurchaseOrder(
					orderId,
					subDomain,
					venueChainId
				)
				if (res?.data) {
					state.purchasedOrder = res.data
					const { errorData } = res.data

					if (errorData && isWebVersionAllowSmoothTransition.value) {
						if (PAYMENT_FAILED_CODES_TICKETING.includes(errorData.errorCode)) {
							showPaymentFailedModal()
						} else {
							showServiceErrorModal()
						}
					} else {
						if (state.purchasedOrder.status === OrderStatus.FAILED_PAY ||
							state.purchasedOrder.status === OrderStatus.PARTIALLY_PAID) {
							showPaymentFailedModal()
						}
					}
				}
			} catch (e) {
				const error = e as CatchError
				const validationCode = getValidationCode(error)
				if (
					error instanceof AxiosError &&
					(error?.response?.status === 500 || validationCode === ValidationMessageEnum.TICKETS_NOT_FOUND)
				) {
					showServiceErrorModal()
				}
			} finally {
				if (isLoading.value) {
					isLoading.value = false
				}
			}
		}

		const goToOrderCreation = (link: string): void => {
			window.location.href = link
		}

		const goBack = (): void => {
			let resolvedUrl = router.resolve({ name: 'event-details', params: { eventId: event.value.id }}).href

			if (!event.value.id) {
				resolvedUrl = router.resolve({ name: 'events' }).href
			}

			window.location.href = resolvedUrl
		}

		const showPaymentFailedModal = (): void => {
			state.unavailablePopupData = {
				title: `${isTicketsPlural.value ? 'Tickets' : 'Ticket'} Purchase Unsuccessful`,
				additionalText: 'Total amount could not be processed'
			}

			paymentFailed.value = true
		}

		const showServiceErrorModal = (): void => {
			state.unavailablePopupData = {
				title: `${isTicketsPlural.value ? 'Tickets' : 'Ticket'} Purchase Unsuccessful`,
				additionalText: 'Ticketing service is experiencing errors'
			}

			paymentFailed.value = true
		}

		return {
			venueIcon,
			closeIcon,
			isLoading,
			event,
			tickets,
			venue,
			fullName,
			timeZoneName,
			addressLink,
			paymentFailed,
			state,
			isOrderPending,
			isEventPast,
			goToOrderCreation,
			goBack,
		}
	}
})
